import { createContext, Dispatch, FC, ReactNode, useContext, useReducer, useCallback } from 'react'
import { Student } from '../interfaces/ModuleTypes'

type State = {
    allStudents: Student[]
    allProfiles: any[]
}

const initialState: State = {
    allStudents: [],
    allProfiles: [],
}

enum ActionType {
    SET_ALL_STUDENTS,
    SET_ALL_PROFILES,
}

type Action =
    {
        type: ActionType.SET_ALL_STUDENTS
        payload: Student[]
    } |
    {
        type: ActionType.SET_ALL_PROFILES
        payload: any[]
    }

const DataContext = createContext<{
    dataState: State
    dispatch: Dispatch<Action>
}>({ dataState: initialState, dispatch: () => null })

const dataReducer = (state: State, action: Action) => {
    switch (action.type) {
        case ActionType.SET_ALL_STUDENTS:
            return { ...state, allStudents: action.payload } as State
        case ActionType.SET_ALL_PROFILES:
            return { ...state, allProfiles: action.payload } as State
        default:
            return state
    }
}

const DataContextProvider: FC<{ children: ReactNode }> = ({ children }: { children: ReactNode }) => {
    const [dataState, dispatch] = useReducer(dataReducer, initialState)

    return (
        <DataContext.Provider value={{ dataState, dispatch }}>
            {children}
        </DataContext.Provider>
    )
}

const useDataContext = () => {
    const context = useContext(DataContext)
    if (!context) {
        throw new Error(
            'useDataContext must be used within a DataContextProvider. Wrap a parent component in <DataContextProvider> to fix this error.'
        )
    }

    const { dataState: { allProfiles, allStudents }, dispatch } = context

    const actions = {
        setAllStudents: useCallback((students: Student[]) =>
            dispatch({ type: ActionType.SET_ALL_STUDENTS, payload: students, }), [dispatch]),
        setAllProfiles: useCallback((profiles: any[]) =>
            dispatch({ type: ActionType.SET_ALL_PROFILES, payload: profiles, }), [dispatch]),
    }

    return { allProfiles, allStudents, dataActions: actions }
}

export default useDataContext
export { DataContextProvider }